import React from "react";
import Alert from "react-bootstrap/Alert";
import { useTranslation } from "react-i18next";
import { FormField } from "./FormField";
import { getAddressByCep } from "../../hooks/useCep";

const CEP_LENGTH = 8;

export const CheckoutFormAddress = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const { t } = useTranslation();
  const [searching, setSearching] = React.useState(false);
  const [cepNotFound, setCepNotFound] = React.useState(false);
  const lastRequestedCep = React.useRef(null);

  const fillAddressFromCep = async (cep) => {
    lastRequestedCep.current = cep;
    setSearching(true);
    const address = await getAddressByCep(cep);

    // A slower answer for a CEP the buyer already replaced must not overwrite the address.
    if (lastRequestedCep.current !== cep) {
      return;
    }

    setSearching(false);

    if (address.error) {
      setCepNotFound(true);
      return;
    }

    setCepNotFound(false);
    setFieldValue("address_street", address.street);
    setFieldValue("address_neighborhood", address.neighborhood);
    setFieldValue("address_city", address.city);
    setFieldValue("address_state", address.state);
  };

  const handleCepChange = (event) => {
    handleChange(event);

    const cep = event.target.value.replace(/\D/g, "");

    if (cep.length === CEP_LENGTH) {
      fillAddressFromCep(cep);
    }
  };

  const handleStateChange = (event) => {
    setFieldValue("address_state", event.target.value.toUpperCase());
  };

  const field = (name, label, onChange = handleChange, fieldAttributes) => (
    <FormField
      type="text"
      label={label}
      placeholder={label}
      name={name}
      error={errors?.[name]}
      touched={touched?.[name]}
      handleBlur={handleBlur}
      handleChange={onChange}
      value={values?.[name] || ""}
      fieldAttributes={fieldAttributes}
    />
  );

  return (
    <div className="mb-3">
      <h6 className="text-start">{t("endereco-nota-fiscal")}</h6>
      {field("address_zipcode", t("cep"), handleCepChange, {
        inputMode: "numeric",
        maxLength: 9,
      })}
      {searching && (
        <small className="text-body-secondary">{t("buscando-cep")}</small>
      )}
      {cepNotFound && <Alert variant="warning">{t("cep-nao-encontrado")}</Alert>}
      {field("address_street", t("rua"))}
      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-12 col-lg-4">
            {field("address_number", t("numero"))}
          </div>
          <div className="col-12 col-lg-8">
            {field("address_complement", t("complemento-opcional"))}
          </div>
        </div>
      </div>
      {field("address_neighborhood", t("bairro"))}
      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-12 col-lg-9">
            {field("address_city", t("cidade"))}
          </div>
          <div className="col-12 col-lg-3">
            {field("address_state", t("uf"), handleStateChange, {
              maxLength: 2,
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
